import { useEffect } from 'react';
import { getTelegram } from '../telegram';
import type { ClientInfo } from '../types';

interface Props {
  info: ClientInfo | null;
  onBack: () => void;
  // Этап 20: 'supplier' — экран статуса для представителя поставщика (другой текст,
  // нет кнопки «В каталог», т.к. поставщик не оформляет заказы);
  // 'staff' — сотрудник GreenHouse (есть staff-роль, нет клиента): управление
  // ведётся в веб-панели, в Mini App ему делать нечего. По умолчанию 'client'.
  kind?: 'client' | 'supplier' | 'staff';
}

export default function ClientStatusPage({ info, onBack, kind = 'client' }: Props) {
  const isSupplier = kind === 'supplier';
  const isStaff = kind === 'staff';
  // У поставщика и сотрудника нет каталога — кнопка «Назад» им не нужна.
  const hideBack = isSupplier || isStaff;

  useEffect(() => {
    const tg = getTelegram();
    if (!tg) return;
    if (!hideBack) {
      tg.BackButton.show();
      tg.BackButton.onClick(onBack);
    } else {
      tg.BackButton.hide();
    }
    tg.MainButton.hide();
    return () => { tg.BackButton.offClick(onBack); tg.BackButton.hide(); };
  }, [onBack, hideBack]);

  const status = info?.status;

  const content = (() => {
    if (isStaff) {
      return {
        icon: '👔',
        title: 'Вы вошли как сотрудник GreenHouse',
        text: 'Управление ведётся в веб-панели администратора.',
        color: '#34c759',
      };
    }
    if (isSupplier) {
      if (status === 'approved') return {
        icon: '✅',
        title: 'Регистрация подтверждена',
        text: 'Спасибо! Доступ настроит администратор — он свяжется с вами.',
        color: '#34c759',
      };
      if (status === 'rejected') return {
        icon: '✕',
        title: 'Заявка отклонена',
        text: info?.rejection_comment ?? 'Свяжитесь с менеджером для уточнения причины.',
        color: '#ff3b30',
      };
      if (status === 'blocked') return {
        icon: '🚫',
        title: 'Аккаунт заблокирован',
        text: 'Обратитесь к менеджеру.',
        color: '#ff3b30',
      };
      return {
        icon: '⏳',
        title: 'Заявка на рассмотрении',
        text: 'Мы проверим данные. После одобрения администратор настроит вам доступ.',
        color: '#ff9500',
      };
    }
    // Одобренный клиент попадает на этот экран только когда у него НЕТ активной
    // привязки к точке (иначе он оформлял бы заказ или выбирал точку). Поэтому
    // для статуса approved показываем понятное «точка не назначена», а не
    // «заявка на рассмотрении». Привязку к кофейне делает менеджер/админ.
    if (status === 'approved') return {
      icon: '🏪',
      title: 'Точка не назначена',
      text: 'Ваша заявка одобрена, но вы пока не привязаны ни к одной кофейне. ' +
        'Оформление заказов станет доступно, как только менеджер привяжет вас к точке.',
      color: '#ff9500',
    };
    if (status === 'pending') return {
      icon: '⏳',
      title: 'Заявка на рассмотрении',
      text: 'Ваша заявка на регистрацию находится на рассмотрении. Пока доступны ' +
        'просмотр товаров и добавление в корзину. После прохождения модерации и ' +
        'назначения на точку Вам придёт уведомление в Telegram или на email, и ' +
        'откроется весь функционал.',
      color: '#ff9500',
    };
    if (status === 'rejected') return {
      icon: '✕',
      title: 'Заявка отклонена',
      text: info?.rejection_comment ?? 'Свяжитесь с менеджером для уточнения причины.',
      color: '#ff3b30',
    };
    if (status === 'blocked') return {
      icon: '🚫',
      title: 'Аккаунт заблокирован',
      text: 'Обратитесь к менеджеру.',
      color: '#ff3b30',
    };
    return {
      icon: '⏳',
      title: 'Заявка на рассмотрении',
      text: 'Ваша заявка на регистрацию находится на рассмотрении. Пока доступны ' +
        'просмотр товаров и добавление в корзину. После прохождения модерации и ' +
        'назначения на точку Вам придёт уведомление в Telegram или на email, и ' +
        'откроется весь функционал.',
      color: '#ff9500',
    };
  })();

  return (
    <div className="container" style={{ padding: '48px 24px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <div style={{ fontSize: 56, marginBottom: 20 }}>{content.icon}</div>
      <h3 style={{ margin: '0 0 12px', fontSize: 20, color: content.color }}>{content.title}</h3>
      <p className="hint" style={{ margin: '0 0 32px', lineHeight: 1.5 }}>{content.text}</p>
      {hideBack ? (
        <button
          onClick={() => getTelegram()?.close?.()}
          style={{
            background: 'var(--tg-secondary-bg)',
            border: 'none',
            borderRadius: 12,
            padding: '12px 32px',
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--tg-text)',
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Закрыть
        </button>
      ) : (
        <button
          onClick={onBack}
          style={{
            background: 'var(--tg-secondary-bg)',
            border: 'none',
            borderRadius: 12,
            padding: '12px 32px',
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--tg-text)',
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          В каталог
        </button>
      )}
    </div>
  );
}
